// Environment identity (IM-2409 / E1.1 of the webapp-diagnostics plan, IM-2380):
// the deployment environment the running app is in, set per deployment via the
// REACT_APP_ENV build-time variable (Amplify console). The whole diagnostics plan
// is built on this — until the app can state which environment it is, no
// comparison between environments is possible, which is what nearly every
// diagnostic question reduces to. Consumed by diagnostics, support reports, and
// any env-aware behaviour.
//
// Resolution: an explicitly-set value always wins. When REACT_APP_ENV is unset we
// distinguish local development ('local', where Vite's DEV is true) from a
// deployed build that forgot to set it ('unknown'), so a misconfigured deployment
// is visible rather than silently presenting as a real environment.

// The environments we deploy. The type documents the expected set; it does NOT
// constrain the runtime value — the resolver keeps whatever the deployment set, so
// adding an environment needs no change here.
export type KnownEnvironment =
  | 'local'
  | 'test'
  | 'staging'
  | 'beta'
  | 'production'
  | 'production-ovh'
  | 'hk-staging'
  | 'hk-production'
  | 'unknown';

// The pure decision, separated from import.meta.env so it is unit-testable with
// plain arguments: an explicit value (trimmed) always wins; an absent one resolves
// to 'local' under a dev build and 'unknown' under a deployed build.
export const pickEnvironment = (raw: unknown, isDev: boolean): string => {
  if (typeof raw === 'string' && raw.trim() !== '') return raw.trim();
  return isDev ? 'local' : 'unknown';
};

// Reads the build-time env and applies the decision above.
export const resolveEnvironment = (): string =>
  pickEnvironment(import.meta.env.REACT_APP_ENV, import.meta.env.DEV);

// The running app's environment name. Stable for the life of the page.
export const ENVIRONMENT: string = resolveEnvironment();

// True when the environment was explicitly provided — i.e. not the 'unknown'
// fallback a deployed build lands on when REACT_APP_ENV is missing.
export const isEnvironmentKnown = (): boolean => ENVIRONMENT !== 'unknown';
